import { Box, NativeSelect } from '@chakra-ui/react'
import { useAtomValue } from 'jotai'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { currentSwitchboardIdAtom, extensionsListAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import {
  getNodeSelectFieldStyles,
  nodeSelectIndicatorStyles,
} from './nodeInputStyles'
import { NodeLabel } from './NodeLabel'

export interface SwitchboardSelectorProps {
  /** Current selected switchboard ID */
  value?: string
  /** Callback when switchboard selection changes */
  onChange: (switchboardId: string | undefined) => void
  /** Placeholder text for the select */
  placeholder?: string
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Reusable switchboard selector dropdown component.
 * Automatically filters to only show Switchboard extensions (ExtensionDialPlan).
 * Excludes the current switchboard to prevent infinite loops (per jump-to-dial-plan.md documentation).
 * Exception: Shows current switchboard if it's already selected (allows editing existing nodes).
 *
 * Data Source: ExtensionDialPlan types from extensionsListAtom (Jotai atom)
 * Fetched via: GET /app/v1/extensions/?type=ExtensionDialPlan
 * Documentation: See libraries/switchboard-ui/NODE_DATA_POPULATION/jump-to-dial-plan.md
 */
export const SwitchboardSelector: React.FC<SwitchboardSelectorProps> = ({
  value = '',
  onChange,
  placeholder: placeholderProp,
  label: labelProp,
  t,
}) => {
  const placeholder =
    placeholderProp ??
    text(t, 'switchboard.core.selectSwitchboard', 'Select switchboard...')
  const label =
    labelProp ?? text(t, 'switchboard.core.switchboardLabel', 'Switchboard')
  // Read from Jotai atoms (props are kept in interface for documentation but ignored)
  // Consuming apps should use useSwitchboardDataAtoms to set atoms
  const extensionsList = useAtomValue(extensionsListAtom)
  const currentSwitchboardId = useAtomValue(currentSwitchboardIdAtom)
  const selectedSwitchboardId = value || undefined

  const availableSwitchboards = React.useMemo(() => {
    if (!extensionsList) return []
    // Filter for switchboard extensions (ExtensionDialPlan type)
    return extensionsList.filter((ext) => {
      // Filter by extension type - only ExtensionDialPlan (switchboards/dial plans)
      if (ext.type !== 'ExtensionDialPlan') return false

      // Filter by active status if the property exists
      // Note: Legacy SQL query doesn't filter by active for dial plans, but we filter for consistency
      const extWithActive = ext as ExtensionInstance & { active?: boolean }
      if ('active' in extWithActive) {
        // For existing nodes: Always include selected switchboard even if inactive
        if (selectedSwitchboardId && String(ext.id) === selectedSwitchboardId) {
          return true
        }
        // For new nodes: Only include active switchboards
        return extWithActive.active === true
      }

      // Current switchboard exclusion logic (per jump-to-dial-plan.md documentation)
      // Rule: Exclude current switchboard UNLESS it's already selected (allows editing existing nodes)
      // Logic: extension_id != current_dial_plan_id || saved_dial_plan_id == current_dial_plan_id
      if (
        currentSwitchboardId !== undefined &&
        ext.id === currentSwitchboardId
      ) {
        // If this is the current switchboard, only include it if it's already selected
        // This prevents infinite loops for new nodes, but preserves existing selections
        if (selectedSwitchboardId && String(ext.id) === selectedSwitchboardId) {
          return true // Include current switchboard if it's already selected (existing node)
        }
        return false // Exclude current switchboard for new nodes (prevents infinite loops)
      }

      // If active property doesn't exist, include the extension (backward compatibility)
      return true
    })
  }, [extensionsList, currentSwitchboardId, selectedSwitchboardId])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSwitchboardId = e.target.value || undefined
      onChange(newSwitchboardId)
    },
    [onChange],
  )

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLSelectElement>) => {
      // Stop propagation to prevent React Flow from handling the click
      e.stopPropagation()
    },
    [],
  )

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLSelectElement>) => {
      // Stop propagation to prevent React Flow from handling the click
      e.stopPropagation()
    },
    [],
  )

  const showLabel = true // Always show labels

  return (
    <Box position="relative" width="100%">
      <NativeSelect.Root>
        <NativeSelect.Field
          className="nodrag"
          value={value}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          {...getNodeSelectFieldStyles()}
        >
          <option value="">{placeholder}</option>
          {availableSwitchboards.map((switchboard) => (
            <option key={switchboard.id} value={String(switchboard.id)}>
              {switchboard.number} -{' '}
              {switchboard.name ||
                text(t, 'switchboard.core.unnamed', 'Unnamed')}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator {...nodeSelectIndicatorStyles} />
      </NativeSelect.Root>
      {showLabel && <NodeLabel label={label} />}
    </Box>
  )
}
